import { useEffect, useCallback } from 'react';

type ShortcutHandler = () => void;

interface ShortcutConfig {
  /** 快捷键映射，key 为按键名（小写），value 为回调函数 */
  shortcuts: Record<string, ShortcutHandler>;
  /** 是否在输入框内也触发（默认 false） */
  enableInInput?: boolean;
  /** 是否启用（默认 true） */
  enabled?: boolean;
}

/**
 * 通用键盘快捷键 Hook
 *
 * @example
 * useKeyboardShortcuts({
 *   shortcuts: {
 *     '1': () => handleFeedback('again'),
 *     '2': () => handleFeedback('hard'),
 *     '3': () => handleFeedback('good'),
 *     'arrowleft': () => goBack(),
 *     'arrowright': () => goNext(),
 *   },
 *   enabled: !isFinished,
 * });
 */
export function useKeyboardShortcuts({
  shortcuts,
  enableInInput = false,
  enabled = true,
}: ShortcutConfig) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!enabled) return;

      // 检查是否在输入框内
      const target = e.target as HTMLElement;
      const isInputElement =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      if (isInputElement && !enableInInput) {
        return;
      }

      // 获取按键名（小写）
      const key = e.key.toLowerCase();

      // 查找对应的处理函数
      const handler = shortcuts[key];
      if (handler) {
        e.preventDefault();
        handler();
      }
    },
    [shortcuts, enableInInput, enabled]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

/**
 * 用于 textarea 内的快捷键处理
 * 返回一个 onKeyDown 处理函数
 *
 * @example
 * const handleTextareaKeyDown = useTextareaShortcuts({
 *   'enter': () => goNext(),
 *   'a': () => goPrev(),  // 会检查是否与输入冲突
 * });
 *
 * <textarea onKeyDown={handleTextareaKeyDown} />
 */
export function useTextareaShortcuts(
  shortcuts: Record<string, ShortcutHandler>,
  options: {
    /** 当 textarea 为空时才触发的快捷键 */
    onlyWhenEmpty?: string[];
  } = {}
) {
  const { onlyWhenEmpty = [] } = options;

  return useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const key = e.key.toLowerCase();
      const handler = shortcuts[key];

      if (!handler) return;

      const textarea = e.currentTarget;
      const isEmpty = !textarea.value.trim();

      // 如果该快捷键需要 textarea 为空才触发
      if (onlyWhenEmpty.includes(key) && !isEmpty) {
        return;
      }

      // Enter 键特殊处理：不阻止 Shift+Enter（换行）
      if (key === 'enter' && e.shiftKey) {
        return;
      }

      e.preventDefault();
      handler();
    },
    [shortcuts, onlyWhenEmpty]
  );
}

export default useKeyboardShortcuts;
